<template>
  <v-app id="app_overdue_collections">
    <v-container fluid>

      <!-- Loading Overlay -->
      <v-overlay v-model="loading" class="align-center justify-center">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-overlay>

      <!-- Header -->
      <v-row class="pl-2 pr-6">
        <v-col class="mt-3" cols="7">
          <p class="custom_page_main_title">OVERDUE COLLECTIONS</p>
          <p class="custom_page_main_sub_title" style="opacity: 0.6">
            Review overdue collections — approval stages: Area Manager → Regional Manager → Sales Manager
          </p>
        </v-col>
        <v-col class="px-4 mt-5" cols="5">
          <v-row class="custom_tool_bar_style">
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
              <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <!-- Main Card -->
      <v-card class="custom_card_box mt-4" elevation="1" border="sm opacity-1" rounded="lg">

        <v-divider thickness="2"></v-divider>

        <!-- Filters - collapsed by default -->
        <v-expansion-panels v-model="panelOpen" multiple>
          <v-expansion-panel value="filters" elevation="0">
            <v-expansion-panel-title expand-icon="mdi-menu-down">
              <v-icon class="mr-3">mdi-filter-menu-outline</v-icon>
              <span class="text-body-1 font-weight-bold">FILTERS</span>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row class="mt-2" dense>

                <!-- Date (Single date OR Date Range) -->
                <v-col cols="12" md="4" class="py-1">
                  <v-date-input
                    v-model="dates"
                    prepend-inner-icon="$calendar"
                    prepend-icon=""
                    clearable
                    label="Select Date Range"
                    multiple="range"
                    density="comfortable"
                    class="custom_input_field_color"
                    variant="outlined"
                    @click:clear="clearDateRange"
                  ></v-date-input>
                </v-col>

                <!-- Area - API-based -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectDistrict"
                    :items="areaLists"
                    item-title="text"
                    item-value="value"
                    label="Area"
                    placeholder="Select area"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    multiple
                    clearable
                    :loading="areasLoading"
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Sales Rep (searchable: type to filter the options) -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedAgent"
                    :items="agentListsWithDetails"
                    :custom-filter="salesRepFilter"
                    density="comfortable"
                    clearable
                    item-title="text"
                    item-value="value"
                    class="custom_input_field_color"
                    label="Sales Rep"
                    multiple
                    placeholder="Type to search sales representative"
                    variant="outlined"
                    :loading="salesRepsLoading"
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props" :title="undefined">
                        <template v-slot:prepend>
                          <v-checkbox-btn :model-value="props.selected"></v-checkbox-btn>
                        </template>
                        <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                        <v-list-item-subtitle style="color: #01579B; font-size: 11px;">
                          REG NO: {{ item.raw.registration }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </template>
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                        close-icon="mdi-close-circle"
                      >
                        {{ item.raw.text }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Route - Fixed -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedRoute"
                    :items="routeTypeOptions"
                    item-title="text"
                    item-value="value"
                    label="Route"
                    placeholder="Select route"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    multiple
                    clearable
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title || item.text }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Dealer - API-based -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedDealer"
                    :items="dealerListsWithDetails"
                    clearable
                    density="comfortable"
                    item-title="text"
                    item-value="value"
                    label="Dealer"
                    class="custom_input_field_color"
                    multiple
                    placeholder="Select dealer"
                    variant="outlined"
                    :loading="dealersLoading"
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props" :title="undefined">
                        <template v-slot:prepend>
                          <v-checkbox-btn :model-value="props.selected"></v-checkbox-btn>
                        </template>
                        <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                        <v-list-item-subtitle style="color: #01579B; font-size: 11px;">
                          REG NO: {{ item.raw.registration }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </template>
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.raw.text }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Collection Type - Hardcoded values -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedCollectionType"
                    :items="collectionTypeOptions"
                    item-title="text"
                    item-value="value"
                    label="Collection Type"
                    placeholder="Select type"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    multiple
                    clearable
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Piyestra Company -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedCompany"
                    :items="hardcodedCompanyLists"
                    item-title="text"
                    item-value="value"
                    label="Piyestra Company"
                    placeholder="Select company"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    multiple
                    clearable
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Search -->
                <v-col cols="12" md="4" class="py-1">
                  <v-text-field
                    v-model="localSearch"
                    clearable
                    label="Search"
                    placeholder="Search by Receipt ID, Dealer Name, Area, etc."
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                  ></v-text-field>
                </v-col>

              </v-row>
              <v-divider class="mb-3 mt-2"></v-divider>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Table -->
        <v-card-text class="table-wrapper">
          <v-data-table-server
            v-model:page="currentPage"
            :headers="headers"
            :items="overdueCollections"
            :items-per-page="perPage"
            :items-per-page-options="[10, 25, 50, 100]"
            :items-length="totalItems"
            :loading="loading"
            item-value="id"
            class="px-2 custom_table_normal compact-table"
            fixed-header
            height="calc(100vh - 280px)"
            @update:options="onTableOptionsChange"
          >
            <template v-slot:loading>
              <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
            </template>

            <!-- Receipt No -->
            <template v-slot:[`item.receipt_no`]="{ item }">
              <p class="font-weight-medium text-body-2 mt-1 mb-1">
                {{ item.receipt_id || '-' }}
              </p>
            </template>

            <!-- Manual Receipt Number -->
            <template v-slot:[`item.manual_receipt_number`]="{ item }">
              <div>
                <v-chip color="#01579B" variant="flat" size="small" class="font-weight-medium">
                  {{ item.manual_receipt_number || 'N/A' }}
                </v-chip>
              </div>
            </template>

            <!-- Collection Date and Time -->
            <template v-slot:[`item.collection_datetime`]="{ item }">
              <div class="column-content">
                {{ formatDateTime(item.created_at) }}
              </div>
            </template>

            <!-- Area -->
            <template v-slot:[`item.area`]="{ item }">
              <p class="text-body-2 mt-1 mb-1">
                {{ item.area_code || '-' }}
              </p>
            </template>

            <!-- Company Name -->
            <template v-slot:[`item.company_name`]="{ item }">
              <p class="text-body-2 mt-1 mb-1">
                {{ item.ar_company || '-' }}
              </p>
            </template>

            <!-- Sales Executive Code & Name -->
            <template v-slot:[`item.sales_executive`]="{ item }">
              <div>
                <p class="text-body-2 mt-1 mb-0 font-weight-medium">{{ getSalesRepName(item.saleRep_id) || '-' }}</p>
                <p style="color: #01579B; font-size: 11px" class="font-weight-medium mt-0 mb-1">
                  Code: {{ getSalespersonCode(item) || '-' }}
                </p>
              </div>
            </template>

            <!-- Customer Info (No & Name) -->
            <template v-slot:[`item.customer_info`]="{ item }">
              <div>
                <p class="font-weight-bold text-body-2 mt-1 mb-0">{{ item.dealer_name || '-' }}</p>
                <p style="color: #01579B; font-size: 11px" class="mb-1 font-weight-medium">Customer No: {{ item.dealer_code || '-' }}</p>
              </div>
            </template>

            <!-- Customer Dis.Group -->
            <template v-slot:[`item.discount_group`]="{ item }">
              <p class="text-body-2 mt-1 mb-1">{{ getDiscountGroup(item) }}</p>
            </template>

            <!-- Invoice No & Date & Amount -->
            <template v-slot:[`item.invoice_details`]="{ item }">
              <div v-if="item.items && item.items.length > 0">
                <div v-for="(invItem, idx) in item.items" :key="idx" class="mb-1 pb-0" style="border-bottom: 1px dashed #e0e0e0;">
                  <p class="text-body-2 font-weight-medium mb-0">{{ invItem.document_no || '-' }}</p>
                  <p class="text-caption text-grey mb-0">{{ formatDate(invItem.posting_date) || '-' }}</p>
                  <p style="color: #01579B; font-size: 12px;" class="font-weight-medium mb-0">LKR {{ formatNumber(invItem.amount) }}</p>
                </div>
              </div>
              <p v-else class="text-body-2 text-grey mt-1 mb-1">-</p>
            </template>

            <!-- Collection Amount -->
            <template v-slot:[`item.collection_amount`]="{ item }">
              <p style="color: #01579B;" class="text-body-2 font-weight-medium mt-1 mb-1">
                LKR. {{ formatNumber(item.collect_total_amount) }}
              </p>
            </template>

            <!-- Payment Method -->
            <template v-slot:[`item.payment_method`]="{ item }">
              <v-chip
                :color="getPaymentMethodColor(item.payment_method)"
                size="small"
                variant="flat"
                class="font-weight-bold"
              >
                <v-icon start size="small">{{ getPaymentMethodIcon(item.payment_method) }}</v-icon>
                {{ item.payment_method || '-' }}
              </v-chip>
            </template>

            <!-- Receipt Status -->
            <template v-slot:[`item.receipt_status`]="{ item }">
              <v-chip
                :color="getReceiptStatusColor(item.receipt_status)"
                size="small"
                variant="flat"
                class="font-weight-bold"
              >
                <v-icon start size="small">{{ getReceiptStatusIcon(item.receipt_status) }}</v-icon>
                {{ item.receipt_status || '-' }}
              </v-chip>
            </template>

            <!-- Cheque Date -->
            <template v-slot:[`item.cheque_date`]="{ item }">
              <p class="text-body-2 mt-1 mb-1">
                {{ getChequeDate(item) || '-' }}
              </p>
            </template>

            <!-- Cheque No -->
            <template v-slot:[`item.cheque_no`]="{ item }">
              <p class="text-body-2 mt-1 mb-1 font-weight-medium">
                {{ getChequeNumber(item) || '-' }}
              </p>
            </template>

            <!-- Bank Name -->
            <template v-slot:[`item.bank_name`]="{ item }">
              <p class="text-body-2 mt-1 mb-1">
                {{ getBankName(item) || '-' }}
              </p>
            </template>

            <!-- Overdue Days -->
            <template v-slot:[`item.overdue_days`]="{ item }">
              <v-chip
                :color="(item.overdue_count || 0) >= 30 ? 'error' : (item.overdue_count || 0) >= 5 ? 'warning' : 'info'"
                size="small"
                variant="flat"
                class="font-weight-bold"
              >
                {{ getOverdueDisplayText(item) }}
              </v-chip>
            </template>

            <!-- Overdue Reason -->
            <template v-slot:[`item.overdue_reason`]="{ item }">
              <p class="text-body-2 mt-1 mb-1" style="max-width: 180px; white-space: normal;">
                {{ item.overdue_reason || '-' }}
              </p>
            </template>

            <!-- Pending At -->
            <template v-slot:[`item.pending_at`]="{ item }">
              <div>
                <div v-if="item.current_pending_stage" class="mb-1">
                  <v-chip
                    :color="getStageColor(item.current_pending_stage)"
                    size="small"
                    variant="flat"
                    class="font-weight-bold"
                  >
                    {{ formatStageLabel(item.current_pending_stage) }}
                  </v-chip>
                </div>
                <div v-else-if="item.all_stages_approved">
                  <v-chip color="success" size="small" variant="flat" class="font-weight-bold">
                    ALL APPROVED
                  </v-chip>
                </div>
              </div>
            </template>

            <!-- Action -->
            <template v-slot:[`item.action`]="{ item }">
              <div class="d-flex flex-column align-start gap-1 py-1">

                <v-btn
                  v-if="canApproveItem(item)"
                  class="custom_approve_btn mb-1"
                  variant="flat"
                  size="small"
                  style="min-width: 130px; justify-content: flex-start;"
                  @click="approveItem(item)"
                >
                  <v-icon size="small" class="mr-1">mdi-check</v-icon>
                  APPROVE
                </v-btn>

                <v-btn
                  class="custom_view_btn mb-1"
                  variant="flat"
                  size="small"
                  style="min-width: 130px; justify-content: flex-start;"
                  @click="viewItem(item)"
                >
                  <v-icon size="small" class="mr-1">mdi-eye</v-icon>
                  VIEW
                </v-btn>

                <v-btn
                  class="custom_view_workflow_btn"
                  variant="outlined"
                  size="small"
                  style="min-width: 130px; justify-content: flex-start;"
                  @click="viewWorkflow(item)"
                >
                  <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
                  WORKFLOW
                </v-btn>

              </div>
            </template>

          </v-data-table-server>
        </v-card-text>

      </v-card>

      <!-- Export Buttons at Bottom -->
      <v-row class="mt-3 mb-2 pl-2 pr-6" justify="end">
        <v-col cols="auto">
          <v-btn class="custom_pdf_btn" variant="outlined" @click="exportPDF" :loading="exporting">
            <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn class="custom_excel_btn" variant="outlined" @click="exportExcel" :loading="exporting">
            <v-icon size="large">mdi-file-excel</v-icon>&nbsp;&nbsp;Excel
          </v-btn>
        </v-col>
      </v-row>

      <!-- Approve Dialog -->
      <v-dialog v-model="approveDialog" max-width="500" scrollable>
        <v-card rounded="md" max-height="560" class="d-flex flex-column">
          <div class="pa-4" style="overflow-y: auto;">

            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  APPROVE OVERDUE COLLECTION
                </v-card-title>
              </template>
              <template v-slot:append>
                <v-btn
                  @click="closeApproveDialog"
                  icon="mdi-close"
                  size="x-small"
                  variant="text"
                ></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>

                <v-card-title style="color: #2E7D32" class="text-body-1 font-weight-medium">
                  <v-icon color="success" size="small">mdi-check-circle</v-icon>
                  &nbsp;&nbsp;Approve at {{ formatStageLabel(itemToApprove?.current_pending_stage) }} stage?
                </v-card-title>

                <v-row class="mt-3" dense>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Receipt ID</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ itemToApprove?.receipt_id || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Manual Receipt No</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ itemToApprove?.manual_receipt_number || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Current Stage</div>
                    <v-chip :color="getStageColor(itemToApprove?.current_pending_stage)" size="x-small" variant="flat">
                      {{ formatStageLabel(itemToApprove?.current_pending_stage) }}
                    </v-chip>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Customer</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ itemToApprove?.items?.[0]?.name || '-' }}
                    </div>
                  </v-col>

                  <!-- ✅ Customer Dis.Group (discount_posting_group) -->
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Customer Dis.Group</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ getDiscountGroup(itemToApprove) }}
                    </div>
                  </v-col>

                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Amount</div>
                    <div class="text-body-2 font-weight-medium" style="color: #01579B">
                      LKR. {{ formatNumber(itemToApprove?.collect_total_amount) }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Overdue Count</div>
                    <v-chip
                      :color="(itemToApprove?.overdue_count || 0) >= 30 ? 'error' : (itemToApprove?.overdue_count || 0) >= 5 ? 'warning' : 'info'"
                      size="x-small"
                      variant="flat"
                    >
                      {{ itemToApprove?.overdue_count || 0 }}
                    </v-chip>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Overdue Label</div>
                    <v-chip
                      :color="(itemToApprove?.overdue_count || 0) >= 30 ? 'error' : (itemToApprove?.overdue_count || 0) >= 5 ? 'warning' : 'info'"
                      size="x-small"
                      variant="flat"
                    >
                      {{ getOverdueDisplayText(itemToApprove) }}
                    </v-chip>
                  </v-col>

                  <!-- Payment Method -->
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Payment Method</div>
                    <v-chip
                      :color="getPaymentMethodColor(itemToApprove?.payment_method)"
                      size="x-small"
                      variant="flat"
                      class="font-weight-bold"
                    >
                      <v-icon start size="x-small">{{ getPaymentMethodIcon(itemToApprove?.payment_method) }}</v-icon>
                      {{ itemToApprove?.payment_method || '-' }}
                    </v-chip>
                  </v-col>

                  <v-col cols="12" class="mt-2">
                    <div class="text-caption text-grey">Overdue Reason</div>
                    <div class="text-body-2">
                      {{ itemToApprove?.overdue_reason || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Receipt Status</div>
                    <v-chip
                      :color="getReceiptStatusColor(itemToApprove?.receipt_status)"
                      size="x-small"
                      variant="flat"
                    >
                      {{ itemToApprove?.receipt_status || '-' }}
                    </v-chip>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Cheque Date</div>
                    <div class="text-body-2">
                      {{ getChequeDate(itemToApprove) || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="4" class="mt-2">
                    <div class="text-caption text-grey">Cheque No</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ getChequeNumber(itemToApprove) || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="4" class="mt-2">
                    <div class="text-caption text-grey">Bank Name</div>
                    <div class="text-body-2">
                      {{ getBankName(itemToApprove) || '-' }}
                    </div>
                  </v-col>
                </v-row>

                <v-textarea
                  v-model="approvalComment"
                  label="Comment (optional)"
                  placeholder="Enter your approval comment here..."
                  rows="3"
                  variant="outlined"
                  density="comfortable"
                  class="mt-4"
                  clearable
                  counter="1000"
                  maxlength="1000"
                ></v-textarea>

                <v-divider class="mt-2"></v-divider>

                <div class="float-end mt-4 mb-2">
                  <v-btn class="custom_approve_btn" @click="approveItemConfirm" :loading="approving">
                    APPROVE
                  </v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="closeApproveDialog">
                    CANCEL
                  </v-btn>
                </div>

              </v-card-item>
            </v-card>

          </div>
        </v-card>
      </v-dialog>

      <!-- Workflow Dialog -->
      <v-dialog v-model="workflowDialog" max-width="600" scrollable>
        <v-card rounded="md" max-height="700" class="d-flex flex-column">
          <div class="pa-4" style="overflow-y: auto;">

            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  APPROVAL WORKFLOW
                </v-card-title>
              </template>
              <template v-slot:append>
                <v-btn @click="closeWorkflowDialog" icon="mdi-close" size="x-small" variant="text"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>

                <v-row class="mt-3" dense>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Receipt ID</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ itemToView?.receipt_id || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Manual Receipt No</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ itemToView?.manual_receipt_number || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Customer</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ itemToView?.items?.[0]?.name || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Amount</div>
                    <div class="text-body-2 font-weight-medium" style="color: #01579B">
                      LKR. {{ formatNumber(itemToView?.collect_total_amount) }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Receipt Status</div>
                    <v-chip
                      :color="getReceiptStatusColor(itemToView?.receipt_status)"
                      size="x-small"
                      variant="flat"
                    >
                      {{ itemToView?.receipt_status || '-' }}
                    </v-chip>
                  </v-col>
                  <v-col cols="4" class="mt-2">
                    <div class="text-caption text-grey">Cheque Date</div>
                    <div class="text-body-2">
                      {{ getChequeDate(itemToView) || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="4" class="mt-2">
                    <div class="text-caption text-grey">Cheque No</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ getChequeNumber(itemToView) || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="4" class="mt-2">
                    <div class="text-caption text-grey">Bank Name</div>
                    <div class="text-body-2">
                      {{ getBankName(itemToView) || '-' }}
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <div class="text-subtitle-2 font-weight-bold mb-4">Approval Workflow</div>

                <!-- Stage 1: Area Manager -->
                <v-card
                  v-if="itemToView?.stages_info?.AREA_MANAGER"
                  :color="itemToView?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? '#e8f5e9' : '#fff3e0'"
                  flat
                  class="mb-3 pa-3 border-left-4"
                  :style="{ 'border-left-color': itemToView?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? '#4caf50' : '#ff9800' }"
                >
                  <div class="text-body-2 font-weight-bold">
                    <v-icon
                      :color="itemToView?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                      :icon="itemToView?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? 'mdi-check-circle' : 'mdi-clock'"
                      size="small" class="mr-2"
                    ></v-icon>
                    AREA MANAGER
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <v-row dense class="mt-2">
                    <v-col cols="6">
                      <div class="text-caption text-grey">Status</div>
                      <v-chip
                        :color="itemToView?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                        size="x-small" variant="flat"
                      >{{ itemToView?.stages_info?.AREA_MANAGER?.status }}</v-chip>
                    </v-col>
                    <v-col cols="6" v-if="itemToView?.stages_info?.AREA_MANAGER?.approved_by">
                      <div class="text-caption text-grey">Approved By</div>
                      <div class="text-body-2">{{ itemToView?.stages_info?.AREA_MANAGER?.approved_by }}</div>
                    </v-col>
                  </v-row>
                  <div v-if="itemToView?.stages_info?.AREA_MANAGER?.approved_at" class="text-caption text-grey mt-2">
                    {{ formatDate(itemToView?.stages_info?.AREA_MANAGER?.approved_at) }}
                  </div>
                  <div v-if="itemToView?.stages_info?.AREA_MANAGER?.comment" class="mt-2 pa-2 bg-grey-lighten-4 rounded text-body-2">
                    <strong>Comment:</strong> {{ itemToView?.stages_info?.AREA_MANAGER?.comment }}
                  </div>
                </v-card>

                <div class="text-center mb-3">
                  <v-icon color="grey">mdi-arrow-down</v-icon>
                </div>

                <!-- Stage 2: Regional Manager -->
                <v-card
                  v-if="itemToView?.stages_info?.REGIONAL_MANAGER"
                  :color="itemToView?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? '#e8f5e9' : '#fff3e0'"
                  flat
                  class="mb-3 pa-3 border-left-4"
                  :style="{ 'border-left-color': itemToView?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? '#4caf50' : '#ff9800' }"
                >
                  <div class="text-body-2 font-weight-bold">
                    <v-icon
                      :color="itemToView?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                      :icon="itemToView?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? 'mdi-check-circle' : 'mdi-clock'"
                      size="small" class="mr-2"
                    ></v-icon>
                    REGIONAL MANAGER
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <v-row dense class="mt-2">
                    <v-col cols="6">
                      <div class="text-caption text-grey">Status</div>
                      <v-chip
                        :color="itemToView?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                        size="x-small" variant="flat"
                      >{{ itemToView?.stages_info?.REGIONAL_MANAGER?.status }}</v-chip>
                    </v-col>
                    <v-col cols="6" v-if="itemToView?.stages_info?.REGIONAL_MANAGER?.approved_by">
                      <div class="text-caption text-grey">Approved By</div>
                      <div class="text-body-2">{{ itemToView?.stages_info?.REGIONAL_MANAGER?.approved_by }}</div>
                    </v-col>
                  </v-row>
                  <div v-if="itemToView?.stages_info?.REGIONAL_MANAGER?.approved_at" class="text-caption text-grey mt-2">
                    {{ formatDate(itemToView?.stages_info?.REGIONAL_MANAGER?.approved_at) }}
                  </div>
                  <div v-if="itemToView?.stages_info?.REGIONAL_MANAGER?.comment" class="mt-2 pa-2 bg-grey-lighten-4 rounded text-body-2">
                    <strong>Comment:</strong> {{ itemToView?.stages_info?.REGIONAL_MANAGER?.comment }}
                  </div>
                </v-card>

                <div class="text-center mb-3">
                  <v-icon color="grey">mdi-arrow-down</v-icon>
                </div>

                <!-- Stage 3: Sales Manager -->
                <v-card
                  v-if="itemToView?.stages_info?.SALES_MANAGER"
                  :color="itemToView?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? '#e8f5e9' : '#fff3e0'"
                  flat
                  class="pa-3 border-left-4"
                  :style="{ 'border-left-color': itemToView?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? '#4caf50' : '#ff9800' }"
                >
                  <div class="text-body-2 font-weight-bold">
                    <v-icon
                      :color="itemToView?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                      :icon="itemToView?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? 'mdi-check-circle' : 'mdi-clock'"
                      size="small" class="mr-2"
                    ></v-icon>
                    SALES MANAGER
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <v-row dense class="mt-2">
                    <v-col cols="6">
                      <div class="text-caption text-grey">Status</div>
                      <v-chip
                        :color="itemToView?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                        size="x-small" variant="flat"
                      >{{ itemToView?.stages_info?.SALES_MANAGER?.status }}</v-chip>
                    </v-col>
                    <v-col cols="6" v-if="itemToView?.stages_info?.SALES_MANAGER?.approved_by">
                      <div class="text-caption text-grey">Approved By</div>
                      <div class="text-body-2">{{ itemToView?.stages_info?.SALES_MANAGER?.approved_by }}</div>
                    </v-col>
                  </v-row>
                  <div v-if="itemToView?.stages_info?.SALES_MANAGER?.approved_at" class="text-caption text-grey mt-2">
                    {{ formatDate(itemToView?.stages_info?.SALES_MANAGER?.approved_at) }}
                  </div>
                  <div v-if="itemToView?.stages_info?.SALES_MANAGER?.comment" class="mt-2 pa-2 bg-grey-lighten-4 rounded text-body-2">
                    <strong>Comment:</strong> {{ itemToView?.stages_info?.SALES_MANAGER?.comment }}
                  </div>
                </v-card>

                <div class="mt-4">
                  <v-btn class="custom_btn_secondary" @click="closeWorkflowDialog" block>
                    CLOSE
                  </v-btn>
                </div>

              </v-card-item>
            </v-card>

          </div>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar" :timeout="5000" :color="snackbarColor">
        {{ snackbarText }}
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>

    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import { EventBus } from '@/plugins/event-bus';
import { mapActions } from 'vuex';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import _ from 'lodash';

const BASE_URL = process.env.VUE_APP_API_BASE_URL;

const SESSION_KEY = 'overdueCollectionsFilters';

const ROLE_STAGE_MAP = {
  'AREA MANAGER':     'AREA_MANAGER',
  'REGIONAL MANAGER': 'REGIONAL_MANAGER',
  'SALES MANAGER':    'SALES_MANAGER',
};

const VIEW_ONLY_ROLES = [
  'ADMINISTRATOR',
  'AREA ACCOUNTANT',
];

export default {
  name: 'OverdueCollections',

  data() {
    return {
      panelOpen: [], // Filters collapsed by default
      dates: [],
      localSearch: '',
      selectedAgent: [],
      selectedRoute: [],
      selectedDealer: [],
      selectDistrict: [],
      selectedCollectionType: [],
      selectedCompany: [],

      approveDialog: false,
      itemToApprove: null,
      approvalComment: '',

      workflowDialog: false,
      itemToView: null,

      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      loading: false,
      approving: false,
      exporting: false,
      salesRepsLoading: false,
      dealersLoading: false,
      areasLoading: false,

      overdueCollections: [],
      paginationInfo: null,
      currentPage: 1,
      perPage: 10,
      totalItems: 0,

      // ── Restore / persistence control flags ──────────────────────────
      // true while filters/page are being restored from sessionStorage.
      // Filter watchers are muted during this time so they DON'T reset
      // the table back to page 1.
      isRestoring: false,
      // true once the very first data fetch has completed. Until then the
      // table's own @update:options events are ignored (Vuetify can emit
      // a page-1 event while items-length is still 0).
      initialLoadDone: false,

      userId: null,
      userType: null,
      userStage: null,
      canViewData: false,

      // Filter dropdown data
      agentListsWithDetails: [],
      dealerListsWithDetails: [],
      areaLists: [],
      salesRepMap: {},

      routeTypeOptions: [
        { text: 'Assign Route Plan', value: 'Assign Route Plan' },
        { text: 'Direct', value: 'Direct' },
        { text: 'Own Route Plan', value: 'Own Route Plan' },
      ],

      hardcodedCompanyLists: [
        { text: 'Piyestra Furniture (Pvt) Ltd', value: 'Piyestra Furniture (Pvt) Ltd', title: 'Piyestra Furniture (Pvt) Ltd' },
        { text: 'D.R.Industries (Pvt) Ltd', value: 'D.R.Industries (Pvt) Ltd', title: 'D.R.Industries (Pvt) Ltd' },
        { text: 'D.R.Home Appliances (Pvt) Ltd', value: 'D.R.Home Appliances (Pvt) Ltd', title: 'D.R.Home Appliances (Pvt) Ltd' }
      ],

      collectionTypeOptions: [
        { text: 'CASH', value: 'CASH', title: 'CASH' },
        { text: 'BANK', value: 'BANK', title: 'BANK' },
        { text: 'CHEQUE', value: 'CHEQUE', title: 'CHEQUE' },
      ],

      headers: [
        { title: 'RECEIPT NO', value: 'receipt_no', sortable: true },
        { title: 'MANUAL RECEIPT NO', value: 'manual_receipt_number', sortable: false },
        { title: 'AREA', value: 'area', sortable: true },
        { title: 'COMPANY NAME', value: 'company_name', sortable: true },
        { title: 'SALES EXECUTIVE', value: 'sales_executive', sortable: true },
        { title: 'DEALER INFO', value: 'customer_info', sortable: false },
        { title: 'CUSTOMER DIS.GROUP', value: 'discount_group', sortable: false },
        { title: 'INVOICE DETAILS', value: 'invoice_details', sortable: false },
        { title: 'COLLECTION DATE & TIME', value: 'collection_datetime', sortable: true },
        { title: 'COLLECTION AMOUNT (LKR)', value: 'collection_amount', sortable: true },
        { title: 'PAYMENT METHOD', value: 'payment_method', sortable: true },
        { title: 'RECEIPT STATUS', value: 'receipt_status', sortable: true },
        { title: 'CHEQUE DATE', value: 'cheque_date', sortable: true },
        { title: 'CHEQUE NO', value: 'cheque_no', sortable: true },
        { title: 'BANK NAME', value: 'bank_name', sortable: true },
        { title: 'OVERDUE REASON', value: 'overdue_reason', sortable: false },
        { title: 'PENDING AT', value: 'pending_at', sortable: false },
        { title: 'ACTION', value: 'action', sortable: false },
      ],
    };
  },

  computed: {

    userRoleLabel() {
      return this.userType || 'Unknown Role';
    },

    roleChipColor() {
      const colors = {
        'AREA MANAGER':     'green-darken-1',
        'REGIONAL MANAGER': 'blue-darken-1',
        'SALES MANAGER':    'purple-darken-1',
        'ADMINISTRATOR':    'orange-darken-1',
        'AREA ACCOUNTANT':  'teal-darken-1',
      };
      return colors[this.userType] || 'grey';
    },
  },

  methods: {
    ...mapActions(['updateSharedDataOverdueCollection']),

    /* ------------------------------------------------------------------ */
    /*  SALES REP DROPDOWN SEARCH FILTER                                  */
    /*  Matches typed text against the rep name AND the REG NO (userCode) */
    /* ------------------------------------------------------------------ */
    salesRepFilter(value, query, item) {
      if (query === null || query === undefined) return true;

      const search = String(query).toLowerCase().trim();
      if (!search) return true;

      const name = String(item?.raw?.text ?? value ?? '').toLowerCase();
      const regNo = String(item?.raw?.registration ?? '').toLowerCase();

      return name.includes(search) || regNo.includes(search);
    },

    /* ------------------------------------------------------------------ */
    /*  DATE FILTER HELPER  (single date OR date range)                   */
    /*                                                                    */
    /*  - 1 date selected  -> from = to = that date  (single date filter) */
    /*  - 2+ dates selected -> from = earliest, to = latest (range)       */
    /*  - nothing selected -> null (no date filter)                       */
    /* ------------------------------------------------------------------ */
    getDateRange() {
      let list = [];

      if (Array.isArray(this.dates)) {
        list = this.dates;
      } else if (this.dates) {
        list = [this.dates];
      }

      const valid = list
        .map(d => (d instanceof Date ? d : new Date(d)))
        .filter(d => d && !isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());

      if (valid.length === 0) return null;

      const from = this.formatDateForAPI(valid[0]);
      const to = this.formatDateForAPI(valid[valid.length - 1]);

      if (!from || !to) return null;

      return { from, to };
    },

    /* ------------------------------------------------------------------ */
    /*  PERSISTENCE HELPERS (sessionStorage)                              */
    /* ------------------------------------------------------------------ */
    _saveFiltersToSession() {
      // Don't overwrite the saved state while we are still restoring it
      if (this.isRestoring) return;

      const filters = {
        dates: this.dates,
        localSearch: this.localSearch,
        selectedAgent: this.selectedAgent,
        selectedRoute: this.selectedRoute,
        selectedDealer: this.selectedDealer,
        selectDistrict: this.selectDistrict,
        selectedCollectionType: this.selectedCollectionType,
        selectedCompany: this.selectedCompany,
        currentPage: this.currentPage,
        perPage: this.perPage,
        totalItems: this.totalItems,
        panelOpen: this.panelOpen,
      };

      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(filters));
      } catch (e) {
        console.warn('Failed to save filters', e);
      }
    },

    _restoreFiltersFromSession() {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (!saved) return false;
      try {
        const filters = JSON.parse(saved);

        // Dates are stored as ISO strings by JSON — turn them back into Date objects
        // (works for a single date AND for a range)
        if (Array.isArray(filters.dates)) {
          this.dates = filters.dates
            .map(d => new Date(d))
            .filter(d => !isNaN(d.getTime()));
        }
        if (filters.localSearch !== undefined) this.localSearch = filters.localSearch;
        if (filters.selectedAgent) this.selectedAgent = filters.selectedAgent;
        if (filters.selectedRoute) this.selectedRoute = filters.selectedRoute;
        if (filters.selectedDealer) this.selectedDealer = filters.selectedDealer;
        if (filters.selectDistrict) this.selectDistrict = filters.selectDistrict;
        if (filters.selectedCollectionType) this.selectedCollectionType = filters.selectedCollectionType;
        if (filters.selectedCompany) this.selectedCompany = filters.selectedCompany;
        if (filters.currentPage) this.currentPage = filters.currentPage;
        if (filters.perPage) this.perPage = filters.perPage;
        // Restoring totalItems keeps the table's page count valid from the very first render,
        // so Vuetify does not clamp the restored page (e.g. 3) back down to 1.
        if (filters.totalItems) this.totalItems = filters.totalItems;
        if (filters.panelOpen) this.panelOpen = filters.panelOpen;
        return true;
      } catch (e) {
        console.warn('Failed to restore filters', e);
        return false;
      }
    },

    parseJsonArrayField(value) {
      if (!value) return null;
      try {
        if (typeof value === 'string' && value.trim().startsWith('[')) {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed;
          }
          return [value];
        }
        if (Array.isArray(value)) {
          return value;
        }
        return [value];
      } catch (e) {
        return [value];
      }
    },

    getChequeNumberForInvoice(invoice, invoiceIndex) {
      if (!invoice) return '-';

      const chequeNumber = invoice.cheque_number;
      if (!chequeNumber) return '-';

      try {
        if (typeof chequeNumber === 'string' && chequeNumber.trim().startsWith('[')) {
          const parsed = JSON.parse(chequeNumber);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[invoiceIndex] !== undefined ? parsed[invoiceIndex] : parsed[0];
          }
        }
        if (Array.isArray(chequeNumber) && chequeNumber.length > 0) {
          return chequeNumber[invoiceIndex] !== undefined ? chequeNumber[invoiceIndex] : chequeNumber[0];
        }
        return chequeNumber;
      } catch (e) {
        return chequeNumber;
      }
    },

    getBankNameForInvoice(invoice, invoiceIndex) {
      if (!invoice) return '-';

      const bankName = invoice.bank_name;
      if (!bankName) return '-';

      try {
        if (typeof bankName === 'string' && bankName.trim().startsWith('[')) {
          const parsed = JSON.parse(bankName);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[invoiceIndex] !== undefined ? parsed[invoiceIndex] : parsed[0];
          }
        }
        if (Array.isArray(bankName) && bankName.length > 0) {
          return bankName[invoiceIndex] !== undefined ? bankName[invoiceIndex] : bankName[0];
        }
        return bankName;
      } catch (e) {
        return bankName;
      }
    },

    getChequeDateForInvoice(invoice, invoiceIndex) {
      if (!invoice) return '-';

      const chequeDate = invoice.cheque_date;
      if (!chequeDate) return '-';

      try {
        if (typeof chequeDate === 'string' && chequeDate.trim().startsWith('[')) {
          const parsed = JSON.parse(chequeDate);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[invoiceIndex] !== undefined ? parsed[invoiceIndex] : parsed[0];
          }
        }
        if (Array.isArray(chequeDate) && chequeDate.length > 0) {
          return chequeDate[invoiceIndex] !== undefined ? chequeDate[invoiceIndex] : chequeDate[0];
        }
        return chequeDate;
      } catch (e) {
        return chequeDate;
      }
    },

    getChequeDate(item) {
      if (!item || !item.items || item.items.length === 0) {
        return null;
      }
      return this.getChequeDateForInvoice(item.items[0], 0);
    },

    getChequeNumber(item) {
      if (!item || !item.items || item.items.length === 0) {
        return null;
      }
      return this.getChequeNumberForInvoice(item.items[0], 0);
    },

    getBankName(item) {
      if (!item || !item.items || item.items.length === 0) {
        return null;
      }
      return this.getBankNameForInvoice(item.items[0], 0);
    },

    getReceiptStatusColor(status) {
      const colors = {
        'COLLECTED': 'green-darken-2',
        'REQUEST REVERSAL': 'orange-darken-2',
        'REVERSED': 'red-darken-2',
        'PENDING': 'blue-darken-2'
      };
      return colors[status] || 'grey-darken-2';
    },

    getReceiptStatusIcon(status) {
      const icons = {
        'COLLECTED': 'mdi-check-circle',
        'REQUEST REVERSAL': 'mdi-replay',
        'REVERSED': 'mdi-close-circle',
        'PENDING': 'mdi-clock'
      };
      return icons[status] || 'mdi-help-circle';
    },

    getSalespersonCode(item) {
      if (!item || !item.items || item.items.length === 0) {
        return null;
      }
      return item.items[0].salesperson_code || null;
    },

    getOverdueDisplayText(item) {
      if (!item) return '-';

      const overdueCount = item.overdue_count;

      if (overdueCount === null || overdueCount === undefined) {
        return '-';
      }

      if (overdueCount === 1) {
        return '1 Day Overdue';
      }

      return `${overdueCount} Days Overdue`;
    },

    formatNumber(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          return `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateForAPI(dateString) {
      if (!dateString) return null;

      if (dateString instanceof Date) {
        const year = dateString.getFullYear();
        const month = String(dateString.getMonth() + 1).padStart(2, '0');
        const day = String(dateString.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      if (typeof dateString === 'string') {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }

      return null;
    },

    clearDateRange() {
      this.dates = [];
    },

    /* ✅ UPDATED: Back button now returns to the PREVIOUS page, not dashboard */
    backPageRoute() {
      this._saveFiltersToSession();

      // If the browser has history to go back to, use it.
      // Otherwise fall back to the dashboard (e.g. direct URL visit / refresh).
      if (window.history.length > 1 && this.$router.options.history.state.back) {
        this.$router.back();
      } else {
        this.$router.push('/dashboard');
      }
    },

    getSalesRepName(saleRepId) {
      return this.salesRepMap[saleRepId] || `ID: ${saleRepId}`;
    },

    /* ================================================================ */
    /*  ✅ FIXED: getDiscountGroup — guards against null/undefined item  */
    /* ================================================================ */
    getDiscountGroup(item) {
      // Guard against null / undefined item (e.g. when dialog is closing)
      if (!item || !item.items || item.items.length === 0) {
        return '-';
      }

      const discountGroup = item.items[0].discount_posting_group;

      if (discountGroup === null || discountGroup === undefined || discountGroup === '') {
        return '-';
      }

      return discountGroup;
    },
    /* ================================================================ */

    getPaymentMethodColor(method) {
      const colors = {
        'CASH': 'green-darken-2',
        'CHEQUE': 'blue-darken-2',
        'BANK': 'orange-darken-2'
      };
      return colors[method] || 'grey-darken-2';
    },

    getPaymentMethodIcon(method) {
      const icons = {
        'CASH': 'mdi-cash',
        'CHEQUE': 'mdi-bank-outline',
        'BANK': 'mdi-bank-transfer'
      };
      return icons[method] || 'mdi-help-circle';
    },

    getStageColor(stage) {
      return {
        AREA_MANAGER:     'green-darken-1',
        REGIONAL_MANAGER: 'blue-darken-1',
        SALES_MANAGER:    'purple-darken-1',
      }[stage] || 'grey';
    },

    formatStageLabel(stage) {
      return {
        AREA_MANAGER:     'AREA MANAGER',
        REGIONAL_MANAGER: 'REGIONAL MANAGER',
        SALES_MANAGER:    'SALES MANAGER',
      }[stage] || stage || '-';
    },

    canApproveItem(item) {
      return (
        this.userStage !== null &&
        item.current_pending_stage === this.userStage &&
        item.stages_info?.[this.userStage]?.status === 'PENDING'
      );
    },

    viewItem(item) {
      const receiptId = item.receipt_id;

      if (receiptId) {
        // Save filters + CURRENT PAGE BEFORE navigating to view page
        this._saveFiltersToSession();
        sessionStorage.setItem('return_from_overdue', 'true');
        this.$router.push(`/total-collections-view/${receiptId}`);
      } else {
        this.showSnackbar('Unable to view: Receipt ID not found', 'error');
      }
    },

    async fetchSalesRepsFromAPI() {
      try {
        this.salesRepsLoading = true;
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId') || 1;

        const url = `${BASE_URL}/api/getSalesRepByUserAreaCodes?userId=${userId}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success && response.data.data) {
          this.agentListsWithDetails = response.data.data.map(rep => ({
            text: rep.salesRep_name,
            value: rep.id,
            registration: rep.userCode,
            id: rep.id,
            userCode: rep.userCode,
            email: rep.email,
            areaCodes: rep.area_codes,
          })).sort((a, b) => a.text.localeCompare(b.text));

          this.salesRepMap = {};
          response.data.data.forEach(rep => {
            this.salesRepMap[rep.id] = rep.salesRep_name;
          });
        } else {
          this.agentListsWithDetails = [];
        }
      } catch (error) {
        console.error('Error fetching sales reps:', error);
        this.agentListsWithDetails = [];
      } finally {
        this.salesRepsLoading = false;
      }
    },

    async fetchDealersFromAPI() {
      try {
        this.dealersLoading = true;
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId') || 1;

        const url = `${BASE_URL}/api/getDealersByUserAreaCodes?userId=${userId}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success && response.data.data) {
          this.dealerListsWithDetails = response.data.data.map(dealer => ({
            text: dealer.dealer_name,
            value: dealer.dealer_code,
            registration: dealer.dealer_code,
          })).sort((a, b) => a.text.localeCompare(b.text));
        } else {
          this.dealerListsWithDetails = [];
        }
      } catch (error) {
        console.error('Error fetching dealers:', error);
        this.dealerListsWithDetails = [];
      } finally {
        this.dealersLoading = false;
      }
    },

    async fetchAreasFromAPI() {
      try {
        this.areasLoading = true;
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId') || 1;

        const url = `${BASE_URL}/api/area/user/${userId}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
          this.areaLists = response.data.data.map(areaCode => ({
            text: areaCode,
            value: areaCode,
            title: areaCode,
          })).sort((a, b) => a.text.localeCompare(b.text));
        } else {
          this.areaLists = [];
        }
      } catch (error) {
        console.error('Error fetching areas:', error);
        this.areaLists = [];
      } finally {
        this.areasLoading = false;
      }
    },

    loadUserInfo() {
      this.userId = localStorage.getItem('userId');

      const rawType = localStorage.getItem('userType') || '';
      this.userType = rawType.toUpperCase().trim();

      this.userStage = ROLE_STAGE_MAP[this.userType] || null;

      if (this.userStage) {
        this.userStage = this.userStage.toUpperCase().trim();
      }

      this.canViewData =
        !!this.userStage ||
        VIEW_ONLY_ROLES.includes(this.userType);

      console.log('=== USER INFO ===');
      console.log('userType:', `"${this.userType}"`);
      console.log('userStage:', `"${this.userStage}"`);
      console.log('canViewData:', this.canViewData);
      console.log('================');
    },

    buildFilterParams() {
      const params = {};
      const userId = this.userId || localStorage.getItem('userId') || 1;

      params.userId = parseInt(userId);

      if (this.userStage) {
        params.stage = this.userStage;
      }

      if (this.selectedAgent && this.selectedAgent.length > 0) {
        params.saleRep_id = this.selectedAgent.join(',');
      }

      if (this.selectedRoute && this.selectedRoute.length > 0) {
        params.route_type = this.selectedRoute.join(',');
      }

      if (this.selectedDealer && this.selectedDealer.length > 0) {
        params.dealer_code = this.selectedDealer.join(',');
      }

      if (this.selectDistrict && this.selectDistrict.length > 0) {
        params.area_code = this.selectDistrict.join(',');
      }

      if (this.selectedCollectionType && this.selectedCollectionType.length > 0) {
        params.payment_method = this.selectedCollectionType.join(',');
      }

      if (this.selectedCompany && this.selectedCompany.length > 0) {
        params.ar_company = this.selectedCompany.join(',');
      }

      // Single date OR date range
      const dateRange = this.getDateRange();
      if (dateRange) {
        params.date_from = dateRange.from;
        params.date_to = dateRange.to;
      }

      if (this.localSearch && this.localSearch.trim().length > 0) {
        params.search = this.localSearch.trim();
      }

      return params;
    },

    async fetchAllFilteredData() {
      const token = localStorage.getItem('token');
      const url = `${BASE_URL}/api/receipt-approvals/pending`;
      const baseParams = this.buildFilterParams();

      const maxPerPage = 500;

      console.log('📤 Fetching all filtered data for export with params:', baseParams);

      try {
        let allData = [];
        let currentPage = 1;
        let lastPage = 1;

        const firstParams = {
          ...baseParams,
          perPage: maxPerPage,
          page: currentPage,
        };

        console.log(`📤 Fetching page ${currentPage}...`);

        const firstResponse = await axios.get(url, {
          params: firstParams,
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        });

        if (firstResponse.data.success && firstResponse.data.data) {
          allData = [...firstResponse.data.data];

          lastPage = firstResponse.data.pagination.last_page;
          console.log(`📊 Total pages: ${lastPage}, Total records: ${firstResponse.data.pagination.total}`);

          if (lastPage > 1) {
            const remainingPages = [];
            for (let page = 2; page <= lastPage; page++) {
              remainingPages.push(page);
            }

            const pagePromises = remainingPages.map(async (page) => {
              const params = {
                ...baseParams,
                perPage: maxPerPage,
                page: page,
              };

              console.log(`📤 Fetching page ${page} of ${lastPage}...`);

              const response = await axios.get(url, {
                params,
                headers: {
                  Authorization: token ? `Bearer ${token}` : '',
                  'Content-Type': 'application/json',
                },
              });

              if (response.data.success && response.data.data) {
                return response.data.data;
              }
              return [];
            });

            const allPageResults = await Promise.all(pagePromises);

            allPageResults.forEach(pageData => {
              allData = [...allData, ...pageData];
            });
          }

          console.log(`✅ Fetched total ${allData.length} records for export`);
          return allData;
        } else {
          console.warn('⚠️ No data found for export');
          return [];
        }
      } catch (error) {
        console.error('❌ Error fetching all filtered data:', error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        throw error;
      }
    },

    getFilterSummary() {
      const filters = [];

      if (this.userRoleLabel) {
        filters.push(`Role: ${this.userRoleLabel}`);
      }
      if (this.userStage) {
        filters.push(`Approval Stage: ${this.formatStageLabel(this.userStage)}`);
      }
      if (this.selectedAgent && this.selectedAgent.length > 0) {
        const agentNames = this.selectedAgent.map(id => {
          const agent = this.agentListsWithDetails.find(a => a.value === id);
          return agent ? agent.text : id;
        });
        filters.push(`Sales Rep: ${agentNames.join(', ')}`);
      }
      if (this.selectedRoute && this.selectedRoute.length > 0) {
        filters.push(`Route: ${this.selectedRoute.join(', ')}`);
      }
      if (this.selectedDealer && this.selectedDealer.length > 0) {
        const dealerNames = this.selectedDealer.map(code => {
          const dealer = this.dealerListsWithDetails.find(d => d.value === code);
          return dealer ? dealer.text : code;
        });
        filters.push(`Dealer: ${dealerNames.join(', ')}`);
      }
      if (this.selectDistrict && this.selectDistrict.length > 0) {
        filters.push(`Area: ${this.selectDistrict.join(', ')}`);
      }
      if (this.selectedCollectionType && this.selectedCollectionType.length > 0) {
        filters.push(`Collection Type: ${this.selectedCollectionType.join(', ')}`);
      }
      if (this.selectedCompany && this.selectedCompany.length > 0) {
        filters.push(`Company: ${this.selectedCompany.length} selected`);
      }

      // Single date OR date range
      const dateRange = this.getDateRange();
      if (dateRange) {
        if (dateRange.from === dateRange.to) {
          filters.push(`Date: ${dateRange.from}`);
        } else {
          filters.push(`Date Range: ${dateRange.from} to ${dateRange.to}`);
        }
      }

      if (this.localSearch && this.localSearch.trim()) {
        filters.push(`Search: "${this.localSearch}"`);
      }

      return filters.length > 0 ? filters.join(' | ') : 'No filters applied';
    },

    /* ------------------------------------------------------------------ */
    /*  LOGO LOADING HELPER                                                */
    /* ------------------------------------------------------------------ */
    loadImageAsBase64(imagePath) {
      return new Promise((resolve) => {
        try {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            try {
              resolve(canvas.toDataURL('image/png'));
            } catch (e) {
              console.warn('Canvas toDataURL failed:', e);
              resolve(null);
            }
          };
          img.onerror = () => {
            console.warn('Failed to load image:', imagePath);
            resolve(null);
          };
          img.src = imagePath;
        } catch (e) {
          console.warn('Error loading image:', e);
          resolve(null);
        }
      });
    },

    async exportPDF() {
      console.log('📄 PDF Export button clicked');

      if (this.$toast) {
        this.$toast.info('Preparing PDF export...');
      }

      this.exporting = true;

      try {
        const allData = await this.fetchAllFilteredData();

        console.log('📊 Data fetched for PDF:', allData ? allData.length : 0, 'records');

        if (!allData || allData.length === 0) {
          if (this.$toast) {
            this.$toast.warning('No data available to export');
          }
          this.exporting = false;
          return;
        }

        // ============================================================
        // Use A3 landscape so all 22 columns fit — nothing gets clipped.
        // A3 landscape = 420mm x 297mm. Usable width ≈ 400mm.
        // ============================================================
        const doc = new jsPDF('landscape', 'mm', 'a3');

        const pageWidth  = doc.internal.pageSize.getWidth();   // ~420
        const pageHeight = doc.internal.pageSize.getHeight();  // ~297

        // ─── Logo section (FIRST PAGE ONLY) ───────────────────────────
        let headerY = 10;

        try {
          const logo1Base64 = await this.loadImageAsBase64(
            require('@/img/company_logo/logo-dark 2.png')
          );
          const logo2Base64 = await this.loadImageAsBase64(
            require('@/img/company_logo/g-sfa.png')
          );

          if (logo1Base64) {
            doc.addImage(logo1Base64, 'PNG', 14, headerY, 40, 15);
          }
          if (logo2Base64) {
            doc.addImage(logo2Base64, 'PNG', 58, headerY, 40, 15);
          }
        } catch (logoError) {
          console.warn('⚠️ Could not load logos for PDF:', logoError);
        }
        // ──────────────────────────────────────────────────────────────

        // Title (centered)
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('Overdue Collections Report', pageWidth / 2, headerY + 5, { align: 'center' });

        // Generated date (centered)
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(
          `Generated on: ${new Date().toLocaleString()}`,
          pageWidth / 2,
          headerY + 13,
          { align: 'center' }
        );

        let filterY = headerY + 22;
        doc.setFontSize(8);
        const filterSummary = this.getFilterSummary();

        // Wrap filter text to available width minus margins
        const splitFilterText = doc.splitTextToSize(
          `Filters applied: ${filterSummary}`,
          pageWidth - 28
        );
        doc.text(splitFilterText, 14, filterY);
        filterY += (splitFilterText.length * 4) + 4;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`Total Records: ${allData.length}`, 14, filterY);
        filterY += 8;

        doc.setFont(undefined, 'normal');

        // ============================================================
        // BUILD TABLE DATA — each row MUST have exactly 22 values
        // ============================================================
        const tableData = [];

        allData.forEach((item, mainIndex) => {
          if (item.items && item.items.length > 0) {
            item.items.forEach((invoice, invoiceIndex) => {
              tableData.push([
                mainIndex + 1,
                item.receipt_id || '-',
                item.manual_receipt_number || '-',
                this.formatDateTime(item.created_at),
                item.area_code || '-',
                item.ar_company || '-',
                this.getSalesRepName(item.saleRep_id) || '-',
                item.dealer_code || '-',
                item.dealer_name || '-',
                this.getDiscountGroup(item),
                invoice.document_no || '-',
                this.formatDate(invoice.posting_date) || '-',
                this.formatNumber(invoice.amount),
                this.formatNumber(item.collect_total_amount),
                item.payment_method || '-',
                item.receipt_status || '-',
                this.getChequeDateForInvoice(invoice, invoiceIndex),
                this.getChequeNumberForInvoice(invoice, invoiceIndex),
                this.getBankNameForInvoice(invoice, invoiceIndex),
                this.getOverdueDisplayText(item),
                item.overdue_reason || '-',
                item.current_pending_stage
                  ? this.formatStageLabel(item.current_pending_stage)
                  : 'ALL APPROVED',
              ]);
            });
          } else {
            tableData.push([
              mainIndex + 1,
              item.receipt_id || '-',
              item.manual_receipt_number || '-',
              this.formatDateTime(item.created_at),
              item.area_code || '-',
              item.ar_company || '-',
              this.getSalesRepName(item.saleRep_id) || '-',
              item.dealer_code || '-',
              item.dealer_name || '-',
              this.getDiscountGroup(item),
              '-',
              '-',
              '-',
              this.formatNumber(item.collect_total_amount),
              item.payment_method || '-',
              item.receipt_status || '-',
              '-',
              '-',
              '-',
              this.getOverdueDisplayText(item),
              item.overdue_reason || '-',
              item.current_pending_stage
                ? this.formatStageLabel(item.current_pending_stage)
                : 'ALL APPROVED',
            ]);
          }
        });

        // ============================================================
        // 22 COLUMN HEADERS — matching the 22 data values exactly
        // ============================================================
        const tableColumns = [
          '#',
          'Receipt ID',
          'Manual Receipt No',
          'Date & Time',
          'Area Code',
          'Company Name',
          'Sales Executive',
          'Customer No',
          'Customer Name',
          'Customer Dis.Group',
          'Invoice No',
          'Invoice Date',
          'Invoice Amount',
          'Collection Amount',
          'Payment Method',
          'Receipt Status',
          'Cheque Date',
          'Cheque No',
          'Bank Name',
          'Overdue Days',
          'Overdue Reason',
          'Pending At',
        ];

        // ============================================================
        // 22 COLUMN STYLES — total ≈ 396mm, fits inside A3 landscape
        // ============================================================
        const columnStyles = {
          0:  { cellWidth: 8,  halign: 'center' },   // #
          1:  { cellWidth: 20 },                     // Receipt ID
          2:  { cellWidth: 20 },                     // Manual Receipt No
          3:  { cellWidth: 20 },                     // Date & Time
          4:  { cellWidth: 12 },                     // Area Code
          5:  { cellWidth: 26 },                     // Company Name
          6:  { cellWidth: 22 },                     // Sales Executive
          7:  { cellWidth: 14 },                     // Customer No
          8:  { cellWidth: 26 },                     // Customer Name
          9:  { cellWidth: 16 },                     // Customer Dis.Group
          10: { cellWidth: 15 },                     // Invoice No
          11: { cellWidth: 15 },                     // Invoice Date
          12: { cellWidth: 18, halign: 'right' },    // Invoice Amount
          13: { cellWidth: 18, halign: 'right' },    // Collection Amount
          14: { cellWidth: 15 },                     // Payment Method
          15: { cellWidth: 17 },                     // Receipt Status
          16: { cellWidth: 15 },                     // Cheque Date
          17: { cellWidth: 15 },                     // Cheque No
          18: { cellWidth: 20 },                     // Bank Name
          19: { cellWidth: 16 },                     // Overdue Days
          20: { cellWidth: 30 },                     // Overdue Reason
          21: { cellWidth: 18 },                     // Pending At
        };

        // Sanity check vs usable page width
        const totalColWidth = Object.values(columnStyles)
          .reduce((sum, c) => sum + (c.cellWidth || 0), 0);
        const usableWidth = pageWidth - 20;
        console.log(`📐 Total column width = ${totalColWidth}mm, usable = ${usableWidth}mm`);
        if (totalColWidth > usableWidth) {
          console.warn('⚠️ Column widths exceed page — AutoTable will scale down.');
        }

        doc.autoTable({
          head: [tableColumns],
          body: tableData,
          startY: filterY,
          styles: {
            fontSize: 6.5,
            cellPadding: 1.2,
            overflow: 'linebreak',
            valign: 'middle',
          },
          headStyles: {
            fillColor: [46, 125, 50],
            textColor: [255, 255, 255],
            fontSize: 7,
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle',
          },
          bodyStyles: {
            textColor: [33, 33, 33],
          },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          columnStyles,
          margin: { left: 10, right: 10, top: 10, bottom: 12 },
          tableWidth: 'wrap',
          showHead: 'everyPage',
          pageBreak: 'auto',
          // Footer with page number
          didDrawPage: () => {
            const pageNum = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(120);
            doc.text(`Page ${pageNum}`, pageWidth - 25, pageHeight - 6);
            doc.setTextColor(0);
          },
        });

        // ⚠️ NO logo loop — logos appear ONLY on the first page

        const fileName =
          `overdue_collections_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
        doc.save(fileName);

        console.log('✅ PDF saved as:', fileName);

        if (this.$toast) {
          this.$toast.success(`PDF exported with ${allData.length} records`);
        }

        this.logDocumentDownload(fileName, 'pdf');

      } catch (error) {
        console.error('❌ Error exporting PDF:', error);
        if (this.$toast) {
          this.$toast.error('Failed to export PDF: ' + (error.message || 'Unknown error'));
        }
      } finally {
        this.exporting = false;
      }
    },

    async exportExcel() {
      console.log('📊 Excel Export button clicked');

      if (this.$toast) {
        this.$toast.info('Preparing Excel export...');
      }

      this.exporting = true;

      try {
        const allData = await this.fetchAllFilteredData();

        console.log('📊 Data fetched for Excel:', allData ? allData.length : 0, 'records');

        if (!allData || allData.length === 0) {
          if (this.$toast) {
            this.$toast.warning('No data available to export');
          }
          this.exporting = false;
          return;
        }

        const wsData = [];

        wsData.push([
          'Receipt ID',
          'Manual Receipt No',
          'Date & Time',
          'Area Code',
          'Company Name',
          'Sales Executive Name',
          'Customer No',
          'Customer Name',
          'Customer Dis.Group',
          'Invoice No',
          'Invoice Date',
          'Invoice Amount',
          'Collection Amount (LKR)',
          'Payment Method',
          'Receipt Status',
          'Cheque Date',
          'Cheque No',
          'Bank Name',
          'Overdue Days',
          'Overdue Reason',
          'Pending At'
        ]);

        allData.forEach((item) => {
          const baseData = {
            receiptId: item.receipt_id || '-',
            manualReceiptNo: item.manual_receipt_number || '-',
            dateTime: this.formatDateTime(item.created_at),
            areaCode: item.area_code || '-',
            companyName: item.ar_company || '-',
            salesExName: this.getSalesRepName(item.saleRep_id) || '-',
            customerNo: item.dealer_code || '-',
            customerName: item.dealer_name || '-',
            customerDisGroup: this.getDiscountGroup(item),
            collectionAmount: this.formatNumber(item.collect_total_amount),
            paymentMethod: item.payment_method || '-',
            receiptStatus: item.receipt_status || '-',
            overdueDays: this.getOverdueDisplayText(item),
            overdueReason: item.overdue_reason || '-',
            pendingAt: item.current_pending_stage ? this.formatStageLabel(item.current_pending_stage) : 'ALL APPROVED'
          };

          if (item.items && item.items.length > 0) {
            item.items.forEach((invoice, invoiceIndex) => {
              wsData.push([
                baseData.receiptId,
                baseData.manualReceiptNo,
                baseData.dateTime,
                baseData.areaCode,
                baseData.companyName,
                baseData.salesExName,
                baseData.customerNo,
                baseData.customerName,
                baseData.customerDisGroup,
                invoice.document_no || '-',
                this.formatDate(invoice.posting_date) || '-',
                this.formatNumber(invoice.amount),
                this.formatNumber(invoice.cash_amount || item.collect_total_amount),
                baseData.paymentMethod,
                baseData.receiptStatus,
                this.getChequeDateForInvoice(invoice, invoiceIndex),
                this.getChequeNumberForInvoice(invoice, invoiceIndex),
                this.getBankNameForInvoice(invoice, invoiceIndex),
                baseData.overdueDays,
                baseData.overdueReason,
                baseData.pendingAt
              ]);
            });
          } else {
            wsData.push([
              baseData.receiptId,
              baseData.manualReceiptNo,
              baseData.dateTime,
              baseData.areaCode,
              baseData.companyName,
              baseData.salesExName,
              baseData.customerNo,
              baseData.customerName,
              baseData.customerDisGroup,
              '-',
              '-',
              '-',
              baseData.collectionAmount,
              baseData.paymentMethod,
              baseData.receiptStatus,
              '-',
              '-',
              '-',
              baseData.overdueDays,
              baseData.overdueReason,
              baseData.pendingAt
            ]);
          }
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        ws['!cols'] = [
          { wch: 18 },
          { wch: 20 },
          { wch: 22 },
          { wch: 12 },
          { wch: 35 },
          { wch: 28 },
          { wch: 15 },
          { wch: 35 },
          { wch: 20 },
          { wch: 18 },
          { wch: 15 },
          { wch: 18 },
          { wch: 22 },
          { wch: 15 },
          { wch: 18 },
          { wch: 15 },
          { wch: 15 },
          { wch: 25 },
          { wch: 15 },
          { wch: 30 },
          { wch: 18 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Overdue Collections');
        const fileName = `overdue_collections_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
        XLSX.writeFile(wb, fileName);

        console.log('✅ Excel saved as:', fileName);

        if (this.$toast) {
          this.$toast.success(`Excel exported with ${allData.length} records`);
        }

        this.logDocumentDownload(fileName, 'excel');

      } catch (error) {
        console.error('❌ Error exporting Excel:', error);
        if (this.$toast) {
          this.$toast.error('Failed to export Excel: ' + (error.message || 'Unknown error'));
        }
      } finally {
        this.exporting = false;
      }
    },

    async fetchOverdueCollections(page = 1) {
      console.log(`📤 Fetching page ${page} of overdue collections`);

      if (!this.canViewData) {
        console.warn('No view access — API call skipped');
        this.overdueCollections = [];
        this.totalItems = 0;
        this.initialLoadDone = true;
        return;
      }

      this.loading = true;
      try {
        const token = localStorage.getItem('token');

        const params = {
          userId: parseInt(this.userId),
          perPage: this.perPage,
          page: page,
        };

        if (this.userStage) {
          params.stage = this.userStage;
        }

        if (this.selectedAgent && this.selectedAgent.length > 0) {
          params.saleRep_id = this.selectedAgent.join(',');
        }

        if (this.selectedRoute && this.selectedRoute.length > 0) {
          params.route_type = this.selectedRoute.join(',');
        }

        if (this.selectedDealer && this.selectedDealer.length > 0) {
          params.dealer_code = this.selectedDealer.join(',');
        }

        if (this.selectDistrict && this.selectDistrict.length > 0) {
          params.area_code = this.selectDistrict.join(',');
        }

        if (this.selectedCollectionType && this.selectedCollectionType.length > 0) {
          params.payment_method = this.selectedCollectionType.join(',');
        }

        if (this.selectedCompany && this.selectedCompany.length > 0) {
          params.ar_company = this.selectedCompany.join(',');
        }

        // Single date OR date range
        const dateRange = this.getDateRange();
        if (dateRange) {
          params.date_from = dateRange.from;
          params.date_to = dateRange.to;
        }

        if (this.localSearch && this.localSearch.trim().length > 0) {
          params.search = this.localSearch.trim();
        }

        console.log('📤 Overdue API Parameters:', params);

        const response = await axios.get(`${BASE_URL}/api/receipt-approvals/pending`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log('📥 API Response:', response.data);

        if (response.data.success) {
          this.overdueCollections = response.data.data || [];
          this.paginationInfo = response.data.pagination || null;
          this.totalItems = this.paginationInfo ? this.paginationInfo.total : 0;

          if (this.paginationInfo && this.paginationInfo.current_page) {
            this.currentPage = this.paginationInfo.current_page;
          } else {
            this.currentPage = page;
          }

          console.log(`✅ Loaded ${this.overdueCollections.length} items for page ${this.currentPage}`);
          console.log('📊 Pagination info:', this.paginationInfo);

          // Safety net: if the restored page no longer exists (e.g. records were
          // approved/removed while the user was away), jump to the last valid page.
          if (
            this.overdueCollections.length === 0 &&
            page > 1 &&
            this.paginationInfo &&
            this.paginationInfo.last_page &&
            page > this.paginationInfo.last_page
          ) {
            console.warn(`⚠️ Page ${page} no longer exists, loading last page ${this.paginationInfo.last_page}`);
            await this.fetchOverdueCollections(this.paginationInfo.last_page);
            return;
          }
        } else {
          throw new Error(response.data.message || 'Failed to fetch data');
        }

      } catch (error) {
        console.error('fetchOverdueCollections error:', error);
        this.showSnackbar(
          error.response?.data?.message || 'Error fetching data.',
          'error'
        );
        this.overdueCollections = [];
        this.paginationInfo = null;
        this.totalItems = 0;
      } finally {
        this.loading = false;
        this.initialLoadDone = true;
        // Always keep the saved page in sync with what is actually displayed
        this._saveFiltersToSession();
      }
    },

    approveItem(item) {
      this.itemToApprove   = item;
      this.approvalComment = '';
      this.approveDialog   = true;
    },

    closeApproveDialog() {
      this.approveDialog   = false;
      this.itemToApprove   = null;
      this.approvalComment = '';
    },

    async approveItemConfirm() {
      this.approving = true;

      try {
        const token      = localStorage.getItem('token');
        const approvalId = this.itemToApprove.id;

        const response = await axios.put(
          `${BASE_URL}/api/receipt-approvals/${approvalId}/approve`,
          {
            userId:  parseInt(this.userId),
            comment: this.approvalComment?.trim() || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          this.closeApproveDialog();
          this.showSnackbar(
            `Approved! Next stage: ${this.formatStageLabel(response.data.data.next_stage)}`,
            'success'
          );
          await this.fetchOverdueCollections(this.currentPage);
          EventBus.emit('overdueCollectionApproved');
        } else {
          throw new Error(response.data.message || 'Approval failed');
        }

      } catch (error) {
        console.error('approveItemConfirm error:', error);
        let msg = 'Error approving. ';

        if (error.response?.status === 400) {
          msg += error.response.data?.message || 'Already approved.';
        } else if (error.response?.status === 404) {
          msg += 'Approval record not found.';
        } else if (error.response?.data?.message) {
          msg += error.response.data.message;
        } else {
          msg += error.message || 'Please try again.';
        }

        this.showSnackbar(msg, 'error');
      } finally {
        this.approving = false;
      }
    },

    viewWorkflow(item) {
      this.itemToView     = item;
      this.workflowDialog = true;
    },

    closeWorkflowDialog() {
      this.workflowDialog = false;
      this.itemToView     = null;
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText  = text;
      this.snackbarColor = color;
      this.snackbar      = true;
    },

    applyFilters() {
      // While restoring saved state, never reset to page 1
      if (this.isRestoring) return;

      console.log('🔄 Applying filters, resetting to page 1');
      this.currentPage = 1;
      this.fetchOverdueCollections(1);
    },

    /**
     * Handles Vuetify's built-in table footer events.
     * Fires when the user changes page OR items-per-page from the footer.
     */
    onTableOptionsChange(options) {
      // Ignore table-generated events until the first load (with the restored page)
      // has completed — otherwise Vuetify's initial/clamped page-1 event would
      // overwrite the restored page.
      if (this.isRestoring || !this.initialLoadDone) return;

      const newPage = options.page;
      const newPerPage = options.itemsPerPage;

      let fetchRequired = false;

      if (this.perPage !== newPerPage) {
        this.perPage = newPerPage;
        this.currentPage = 1;
        fetchRequired = true;
      } else if (this.paginationInfo && this.paginationInfo.current_page !== newPage) {
        this.currentPage = newPage;
        fetchRequired = true;
      } else if (!this.paginationInfo && newPage !== 1 && this.currentPage !== newPage) {
        this.currentPage = newPage;
        fetchRequired = true;
      }

      if (fetchRequired) {
        this.fetchOverdueCollections(this.currentPage);
        this._saveFiltersToSession();
      }
    },

    async logDocumentDownload(fileName, fileType) {
      try {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        await axios.post(
          `${BASE_URL}/api/log-document-download`,
          { user_id: userId, file_type: fileType, file_name: fileName, entity_area: 'Overdue Collections' },
          { headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' } },
        );
      } catch (error) {
        console.error('❌ Error logging download:', error);
      }
    },

    setupEventListeners() {
      EventBus.on('overdueCollectionRefresh', () => {
        this.fetchOverdueCollections(this.currentPage);
      });
    },

    removeEventListeners() {
      EventBus.off('overdueCollectionRefresh');
    },
  },

  watch: {
    // Search is debounced, but the "am I restoring?" check happens immediately
    // (before the debounce) so a restored search text never triggers a page reset.
    localSearch() {
      if (this.isRestoring) return;
      this.debouncedSearch();
    },

    selectedAgent:          { handler() { if (this.isRestoring) return; this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectedRoute:          { handler() { if (this.isRestoring) return; this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectedDealer:         { handler() { if (this.isRestoring) return; this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectDistrict:         { handler() { if (this.isRestoring) return; this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectedCollectionType: { handler() { if (this.isRestoring) return; this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectedCompany:        { handler() { if (this.isRestoring) return; this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    dates:                  { handler() { if (this.isRestoring) return; this.applyFilters(); this._saveFiltersToSession(); }, deep: true },

    panelOpen: {
      handler() {
        this._saveFiltersToSession();
      },
      deep: true,
    },
  },

  created() {
    // Debounced search handler (per-instance)
    this.debouncedSearch = _.debounce(() => {
      console.log('🔎 Search watcher triggered - calling API with search param');
      this.applyFilters();
      this._saveFiltersToSession();
    }, 500);

    // Restore filters + page BEFORE the first render so the table is created
    // already pointing at the saved page. Watchers are muted via isRestoring
    // until Vue has flushed the resulting changes.
    this.isRestoring = true;
    this._restoreFiltersFromSession();
    this.$nextTick(() => {
      this.isRestoring = false;
    });
  },

  mounted() {
    this.loadUserInfo();

    this.setupEventListeners();

    this.fetchSalesRepsFromAPI();
    this.fetchDealersFromAPI();
    this.fetchAreasFromAPI();

    // Fetch data — use the restored page (or 1 if nothing was saved)
    const targetPage = this.currentPage && this.currentPage > 0 ? this.currentPage : 1;
    this.fetchOverdueCollections(targetPage);
  },

  beforeUnmount() {
    // Save filters one last time
    this._saveFiltersToSession();

    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }

    this.removeEventListeners();
  },
};
</script>

<style>
/* Import UserList styles so the pagination footer looks identical */
@import "../../styles/user_management/users_list.scss";
@import "../../styles/collection_management/collection_management.scss";

.custom_approve_btn {
  background-color: #2E7D32 !important;
  color: white !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 0 16px;
  height: 34px;
  box-shadow: 0 2px 4px rgba(46, 125, 50, 0.2);
}

.custom_approve_btn:hover:not(:disabled) {
  background-color: #1B5E20 !important;
  box-shadow: 0 4px 8px rgba(46, 125, 50, 0.3);
}

.custom_approve_btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.custom_view_btn {
  background-color: rgb(128, 0, 128) !important;
  color: white !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 0 16px;
  height: 34px;
  box-shadow: 0 2px 4px rgba(128, 0, 128, 0.2);
}

.custom_view_btn:hover:not(:disabled) {
  background-color: rgb(100, 0, 100) !important;
  box-shadow: 0 4px 8px rgba(128, 0, 128, 0.3);
}

.custom_view_btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.custom_view_workflow_btn {
  border-color: #1976d2 !important;
  color: #1976d2 !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 0 16px;
  height: 34px;
}

.custom_view_workflow_btn:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.action-btn-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 0;
}

.action-btn-group .v-btn {
  min-width: 130px !important;
  width: 130px !important;
  justify-content: flex-start !important;
}

.v-data-table tbody tr:hover {
  background-color: #f5f5f5 !important;
  transition: background-color 0.2s ease;
}

.v-chip {
  font-weight: 500;
  text-transform: uppercase;
  font-size: 11px;
}

.v-list-item-subtitle {
  font-size: 11px !important;
  line-height: 1.2 !important;
  opacity: 0.9;
  margin-top: 0 !important;
}

.custom_btn_secondary {
  background-color: #f0f0f0 !important;
  color: #333 !important;
  font-weight: 500;
  border-radius: 8px;
}

.custom_btn_secondary:hover {
  background-color: #e0e0e0 !important;
}

.border-left-4 {
  border-left: 4px solid !important;
}

.bg-grey-lighten-4 {
  background-color: #f5f5f5;
}

.column-content {
  font-size: 13px;
  color: #616161;
}

/* Table wrapper styles */
.table-wrapper {
  position: relative;
  overflow: hidden;
}

/* Ensure the table container handles scrolling correctly */
.v-data-table .v-table__wrapper {
  overflow-y: auto !important;
  overflow-x: auto !important;
}

/* Keep header fixed */
.v-data-table .v-table__thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
}

/* Compact table styles */
.compact-table :deep(.v-data-table__tr) {
  height: auto !important;
}

.compact-table :deep(.v-data-table__td) {
  padding: 4px 8px !important;
  line-height: 1.2 !important;
  font-size: 0.8rem !important;
}

.compact-table :deep(.v-data-table__th) {
  padding: 6px 8px !important;
  font-size: 0.75rem !important;
}

.compact-table :deep(.v-data-table__td .v-chip) {
  height: 22px !important;
  font-size: 10px !important;
}

.compact-table :deep(.v-data-table__td .v-btn) {
  height: 28px !important;
  font-size: 0.7rem !important;
}

.compact-table :deep(.v-data-table__td p) {
  margin-bottom: 2px !important;
  margin-top: 2px !important;
  line-height: 1.2 !important;
}

/* ============================================================ */
/* FIX: Ensure dialog buttons are always clickable               */
/* Prevents overlay / clipping issues from blocking clicks      */
/* ============================================================ */
.v-dialog .v-card .v-btn,
.v-dialog .v-overlay__content .v-btn {
  pointer-events: auto !important;
  position: relative;
  z-index: 10;
}

.v-overlay--active .v-overlay__content {
  pointer-events: auto !important;
}
</style>